import { useMemo, useState } from "react";
import {
  CloseCircleFilled,
  FileTextOutlined,
  RollbackOutlined,
} from "@ant-design/icons";
import { useQuery } from "@tanstack/react-query";
import { Button, Popover, Table, Tooltip, Typography } from "antd";
import type { ColumnsType } from "antd/es/table";
import type { Dayjs } from "dayjs";
import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import { Navigate } from "react-router-dom";
import { journalsQueries } from "@/entities/journal";
import {
  JournalSectionActions,
  JournalSectionContent,
  JournalSectionFilters,
  JournalSectionWrapper,
} from "@/entities/journal/ui";
import {
  type AmendmentRequest,
  isActiveAmendmentStatus,
  useAmendmentsStore,
} from "@/features/journal-version-list";
import { CommentIcon } from "@/shared/assets/svg/icons";
import { useFlags } from "@/shared/lib/flags";
import { useCoordinator } from "@/shared/lib/hooks";
import { useAppRoutes, useNavigate } from "@/shared/lib/react-router";
import { JournalsAmendmentsFilters } from "../journals-amendments-filters";
import { JournalsAmendmentsPreviewDrawer } from "../journals-amendments-preview-drawer";
import { JournalsAmendmentsRejectionReason } from "../journals-amendments-rejection-reason";
import { JournalsAmendmentsSignedByCell } from "../journals-amendments-signed-by-cell";
import {
  JournalsAmendmentsStatusBadge,
  type JournalsAmendmentsStatus,
} from "../journals-amendments-status-badge";
import "./journals-amendments.scss";

dayjs.extend(utc);
dayjs.extend(timezone);

export const JournalsAmendments = () => {
  const isAmendmentsEnabled = useFlags("ojrFormationFlowEnabledV1");
  const navigate = useNavigate();
  const appRoutes = useAppRoutes();
  const { titleId, projectId, objectId, mainJournalId } = useCoordinator();
  const { isPending: isTitleLoading } = useQuery(
    journalsQueries.getTitleJournal({ titleId }),
  );
  const amendmentRequests = useAmendmentsStore((s) => s.requests);
  const applyAction = useAmendmentsStore((s) => s.applyAction);
  const requestsForTitle = useMemo(() => {
    const scopedRequests = amendmentRequests.filter(
      (request) => request.titleId === titleId,
    );
    return scopedRequests.length > 0
      ? scopedRequests
      : amendmentRequests.filter((request) => request.titleId === "demo-title");
  }, [amendmentRequests, titleId]);
  const hasActiveRequest = amendmentRequests.some(
    (request) =>
      request.titleId === titleId &&
      !request.isSuperseded &&
      isActiveAmendmentStatus(request.status),
  );

  const [selectedRequest, setSelectedRequest] =
    useState<AmendmentRequest | null>(null);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [search, setSearch] = useState("");
  const [authorFilter, setAuthorFilter] = useState<string | null>(null);
  const [dateRange, setDateRange] = useState<[Dayjs | null, Dayjs | null]>([
    null,
    null,
  ]);
  const [statusFilter, setStatusFilter] = useState<JournalsAmendmentsStatus[]>(
    [],
  );
  const [sourceFilter, setSourceFilter] = useState<string | null>(null);
  const [changeTypeFilter, setChangeTypeFilter] = useState<string | null>(null);

  const authorOptions = useMemo(() => {
    const set = new Set<string>();
    requestsForTitle.forEach((request) => {
      if (request.checkedBy !== "—") {
        set.add(request.checkedBy);
      }
    });
    return Array.from(set).sort();
  }, [requestsForTitle]);

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    const [from, to] = dateRange;

    return requestsForTitle.filter((row) => {
      if (authorFilter && row.checkedBy !== authorFilter) {
        return false;
      }
      if (statusFilter.length > 0 && !statusFilter.includes(row.status)) {
        return false;
      }
      if (sourceFilter && row.requestSource !== sourceFilter) {
        return false;
      }
      if (changeTypeFilter && row.changeType !== changeTypeFilter) {
        return false;
      }
      if (from && to) {
        const rowDay = dayjs
          .utc(row.dateIso)
          .tz("Europe/Moscow")
          .startOf("day");
        const fromDay = from.startOf("day");
        const toDay = to.endOf("day");
        if (rowDay.isBefore(fromDay) || rowDay.isAfter(toDay)) {
          return false;
        }
      }
      if (!q) {
        return true;
      }
      const hay = [
        row.changeType,
        row.reason,
        row.checkedBy,
        row.status,
        row.requestSource,
      ]
        .join(" ")
        .toLowerCase();
      return hay.includes(q);
    });
  }, [
    requestsForTitle,
    search,
    authorFilter,
    dateRange,
    statusFilter,
    sourceFilter,
    changeTypeFilter,
  ]);

  const pagedRows = useMemo(() => {
    const start = (page - 1) * limit;
    return [...filteredRows]
      .sort((a, b) => b.requestNumber - a.requestNumber)
      .slice(start, start + limit);
  }, [filteredRows, page, limit]);

  const rejectedRequest =
    filteredRows.length === 1 &&
    filteredRows[0].status === "REJECTED" &&
    !filteredRows[0].isSuperseded
      ? filteredRows[0]
      : null;

  const openRequest = (request: AmendmentRequest) => {
    navigate(
      appRoutes.journals.amendmentsView(
        projectId,
        objectId,
        mainJournalId,
        titleId,
        request.key,
      ),
    );
  };

  const columns: ColumnsType<AmendmentRequest> = [
    {
      title: "№",
      dataIndex: "requestNumber",
      key: "requestNumber",
      width: 64,
      sorter: (a, b) => a.requestNumber - b.requestNumber,
      defaultSortOrder: "descend",
    },
    {
      title: "Тип изменений",
      dataIndex: "changeType",
      key: "changeType",
      width: 168,
    },
    {
      title: "Причина изменения",
      dataIndex: "reason",
      key: "reason",
      width: 250,
      render: (text: string) => <span>{text}</span>,
    },
    {
      title: "Проверил и подписал",
      dataIndex: "checkedBy",
      key: "checkedBy",
      width: 160,
      render: (name: string, record) => (
        <JournalsAmendmentsSignedByCell
          name={name}
          verified={record.checkedVerified}
        />
      ),
    },
    {
      title: "Статус",
      dataIndex: "status",
      key: "status",
      width: 160,
      render: (status: JournalsAmendmentsStatus, record) => (
        <div className="journals-amendments__status-cell">
          <div className="journals-amendments__status-row">
            <JournalsAmendmentsStatusBadge status={status} />
            {status === "REJECTED" ? (
              <Popover
                content={<JournalsAmendmentsRejectionReason request={record} />}
                trigger="click"
                overlayClassName="journals-amendments-rejection-popover"
              >
                <Button
                  type="text"
                  size="small"
                  aria-label="Показать причину отклонения"
                  icon={
                    <span className="journals-amendments__comment-icon">
                      <CommentIcon />
                    </span>
                  }
                  onClick={(event) => event.stopPropagation()}
                />
              </Popover>
            ) : null}
          </div>
          <span>{dayjs(record.updatedAt).format("DD.MM.YYYY")}</span>
        </div>
      ),
    },
    {
      title: "Дата создания",
      dataIndex: "dateIso",
      key: "dateIso",
      width: 160,
      render: (dateIso: string, record) => (
        <div className="journals-amendments__creation-cell">
          <span className="journals-amendments__creation-time">
            {dayjs.utc(dateIso).tz("Europe/Moscow").format("HH:mm")} МСК
          </span>
          <span>
            {dayjs.utc(dateIso).tz("Europe/Moscow").format("DD.MM.YYYY")}
          </span>
          <span className="journals-amendments__request-source">
            {record.requestSource === "SYSTEM"
              ? "Системная заявка"
              : `Создана вручную: ${record.changedBy}`}
          </span>
        </div>
      ),
    },
  ];

  if (!isAmendmentsEnabled) {
    return <Navigate to="../general" replace />;
  }

  return (
    <div className="journals-amendments-page__root">
      {rejectedRequest ? (
        <div className="journals-amendments__rejected-banner">
          <div className="journals-amendments__rejected-message">
            <CloseCircleFilled />
            <span>
              Заявка на изменение отклонена, верните в работу для исправления
            </span>
          </div>
          <div className="journals-amendments__rejected-actions">
            <Popover
              content={
                <JournalsAmendmentsRejectionReason request={rejectedRequest} />
              }
              trigger="click"
              overlayClassName="journals-amendments-rejection-popover"
            >
              <Button icon={<CommentIcon />}>Причина</Button>
            </Popover>
            <Button
              type="primary"
              icon={<RollbackOutlined />}
              onClick={() => applyAction(rejectedRequest.key, "RETURN_TO_WORK")}
            >
              Вернуть в работу
            </Button>
          </div>
        </div>
      ) : null}
      <JournalSectionWrapper>
        <JournalSectionActions>
          <div className="journals-amendments__head">
            <Typography.Title level={4} className="journals-amendments__title">
              Заявки на изменение Титульного листа
            </Typography.Title>
            <Tooltip
              title={
                hasActiveRequest
                  ? "Нельзя создать новую заявку, если есть активная заявка на изменение"
                  : undefined
              }
            >
              <span>
                <Button
                  type="default"
                  icon={<FileTextOutlined />}
                  disabled={hasActiveRequest}
                  onClick={() =>
                    navigate(
                      appRoutes.journals.amendmentsForm(
                        projectId,
                        objectId,
                        mainJournalId,
                        titleId,
                      ),
                    )
                  }
                >
                  Подать заявку
                </Button>
              </span>
            </Tooltip>
          </div>
        </JournalSectionActions>

        <JournalSectionFilters>
          <JournalsAmendmentsFilters
            search={search}
            onSearchChange={(v) => {
              setSearch(v);
              setPage(1);
            }}
            authorOptions={authorOptions}
            authorFilter={authorFilter}
            onAuthorFilter={(v) => {
              setAuthorFilter(v);
              setPage(1);
            }}
            dateRange={dateRange}
            onDateRange={(v) => {
              setDateRange(v);
              setPage(1);
            }}
            statusFilter={statusFilter}
            onStatusFilter={(v) => {
              setStatusFilter(v);
              setPage(1);
            }}
            sourceFilter={sourceFilter}
            onSourceFilter={(value) => {
              setSourceFilter(value);
              setPage(1);
            }}
            changeTypeFilter={changeTypeFilter}
            onChangeTypeFilter={(value) => {
              setChangeTypeFilter(value);
              setPage(1);
            }}
          />
        </JournalSectionFilters>

        <JournalSectionContent>
          <div className="journals-amendments-page__table-host">
            <Table
              className="journals-amendments-table"
              rowKey="key"
              rowClassName={(record) =>
                record.isSuperseded
                  ? "journals-amendments-row journals-amendments-row--superseded"
                  : "journals-amendments-row"
              }
              onRow={(record) => ({
                onClick: () => setSelectedRequest(record),
              })}
              columns={columns}
              dataSource={pagedRows}
              loading={isTitleLoading}
              tableLayout="fixed"
              pagination={{
                total: filteredRows.length,
                current: page,
                pageSize: limit,
                showSizeChanger: true,
                showQuickJumper: true,
                hideOnSinglePage: true,
                pageSizeOptions: ["5", "10", "20", "50", "100"],
                onChange: (nextPage, nextSize) => {
                  setPage(nextSize === limit ? nextPage : 1);
                  setLimit(nextSize);
                },
                showTotal: (total, range) =>
                  `${range[0]}-${range[1]} из ${total} записей`,
              }}
              scroll={{ x: 980 }}
            />
          </div>
        </JournalSectionContent>
      </JournalSectionWrapper>
      <JournalsAmendmentsPreviewDrawer
        request={selectedRequest}
        open={!!selectedRequest}
        onClose={() => setSelectedRequest(null)}
        onOpenRequest={openRequest}
      />
    </div>
  );
};
